if(!customElements.get('bundle-info')) {

  class BundleInfo extends HTMLElement {

    get products() {
      return Array.from(this.querySelectorAll('bundle-product')).filter(product => {
        const hasSize = product.querySelector('[data-type="size"] input:checked');
        return hasSize;
      })
    }

    constructor() {
      super();
    }

    connectedCallback() {
      this.productsCount = this.dataset.productsCount;
      this.bundleDiscount = this.dataset.bundleDiscount;
      this.addToCartButton = this.querySelector('[data-bundle-add-to-cart]');
      this.bundleId = this.dataset.bundleId;
      this.errorMessageWrapper = this.querySelector('[data-error-message-wrapper]');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer') || document.querySelector('mini-cart');

      this.setListeners();
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber && this.variantChangeUnsubscriber();
    }

    setListeners() {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.bundleProductRendered,
        this.onVariantChange.bind(this)
      );

      this.addToCartButton.addEventListener('click', this.handleAddToCart.bind(this));
    }

    /**
     * Resolves the user-facing error text from a cart/add.js response.
     * @param {object} result - Parsed JSON response from cart/add.js.
     * @returns {string}
     */
    getAddToCartErrorText(result = {}) {
      const { description, message } = result;
      const fallback = this.dataset.bundleAddError || window.cartStrings?.error;

      if (description && description !== 'Cart Error') return description;
      if (message && message !== 'Cart Error') return message;

      return fallback;
    }

    /**
     * Shows or hides the bundle add-to-cart error message.
     * @param {string|false} errorMessage - Message to display; falsy hides the alert.
     */
    handleErrorMessage(errorMessage = false) {
      if (!this.errorMessageWrapper) return;

      const errorText = this.errorMessageWrapper.querySelector('[data-error-message]');

      if (!errorMessage) {
        this.errorMessageWrapper.setAttribute('hidden', '');
        if (errorText) errorText.textContent = '';
        return;
      }

      if (errorText) errorText.textContent = errorMessage;
      this.errorMessageWrapper.removeAttribute('hidden');
    }

    clearErrorMessage() {
      this.handleErrorMessage(false);
    }

    /**
     * Builds a stable signature for a set of variant IDs.
     * @param {Array<number|string>} variantIds - Variant IDs to normalize.
     * @returns {string}
     */
    getVariantSignature(variantIds = []) {
      return [...variantIds].map(Number).filter(Boolean).sort((a, b) => a - b).join(',');
    }

    /**
     * Checks whether a bundle with the same variant selection is already in the cart.
     * Bundle line items share a `_bundleId` property (`<bundleId>||<timestamp>`),
     * so items are grouped by that key and compared against the current selection.
     * @param {Array<{id: number}>} items - Items about to be added to the cart.
     * @returns {Promise<boolean>}
     */
    async isBundleInCart(items) {
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`);
        const cart = await response.json();
        const selectedIds = this.getVariantSignature(items.map(({ id }) => id));
        const bundlePrefix = `${this.bundleId}||`;
        const bundleGroups = {};

        cart.items.forEach((item) => {
          const bundleKey = item.properties?._bundleId;
          if (!bundleKey) return;

          // Support both current (`id||ts`) and legacy (`id`) property formats.
          const belongsToBundle =
            bundleKey === String(this.bundleId) || bundleKey.startsWith(bundlePrefix);

          if (!belongsToBundle) return;

          bundleGroups[bundleKey] = bundleGroups[bundleKey] || [];
          bundleGroups[bundleKey].push(item.variant_id);
        });

        return Object.values(bundleGroups).some(
          (variantIds) => this.getVariantSignature(variantIds) === selectedIds
        );
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    async handleAddToCart() {
      if (this.addToCartButton.disabled || this.addToCartButton.getAttribute('aria-busy') === 'true') {
        return;
      }

      // One shared timestamp so every component of this add shares the same `_bundleId`.
      const bundleInstanceId = `${this.bundleId}||${Date.now()}`;
      const items = [];

      this.products.forEach((product) => {
        const checkedSizeInput = product.querySelector('[data-type="size"] input:checked');

        if (!checkedSizeInput) return;

        items.push({
          id: Number(checkedSizeInput.dataset.variantId),
          quantity: 1,
          properties: {
            _bundleId: bundleInstanceId
          }
        });
      });

      if (items.length === 0 || !this.cart) return;

      this.clearErrorMessage();
      this.addToCartButton.classList.add('loading');
      this.addToCartButton.setAttribute('aria-busy', 'true');

      try {
        // Same bundle with the same sizes already in cart: just reopen the minicart.
        if (await this.isBundleInCart(items)) {
          this.cart.open();
          return;
        }

        this.cart.setActiveElement(document.activeElement);

        const body = {
          items,
          sections: this.cart.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname
        };

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });

        const result = await response.json();

        if (!response.ok || result.status) {
          const errorText = this.getAddToCartErrorText(result);

          this.handleErrorMessage(errorText);
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'bundle-info',
            productVariantId: items[0].id,
            errors: result.errors || result.description,
            message: result.message
          });
          return;
        }

        this.clearErrorMessage();

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bundle-info',
          productVariantId: items[0].id,
          cartData: result
        });

        this.cart.classList.contains('is-empty') && this.cart.classList.remove('is-empty');
        this.cart.renderContents(result);
      } catch (error) {
        console.error(error);
        this.handleErrorMessage(this.getAddToCartErrorText());
      } finally {
        this.addToCartButton.classList.remove('loading');
        this.addToCartButton.removeAttribute('aria-busy');
      }
    }

    formatMoney(cents) {
      return `${window.money.symbol}${(cents / 100).toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
    
    onVariantChange() {
      const comparePrice = this.products.reduce((acc, product) => {
        return acc + Number(product.querySelector('[name="price"]').value);
      }, 0);
    
      const newPrice = comparePrice * (1 - this.bundleDiscount / 100);
    
      this.addToCartButton.querySelector('[data-price]').innerHTML = this.formatMoney(newPrice);
      this.addToCartButton.querySelector('[data-compare-price]').innerHTML = this.formatMoney(comparePrice);
      this.addToCartButton.toggleAttribute('disabled', this.products.length != this.productsCount);
    }
  }

  customElements.define('bundle-info', BundleInfo);

}

if(!customElements.get('bundle-product')) {
  class BundleProduct extends HTMLElement {

    get productId() {
      return this.dataset.productId;
    }

    constructor() {
      super();
      this.isLoading = false;
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.onVariantChange.bind(this)
      );
      this.addEventListener('click', this.onClick);
      this.unavailableIconDisabled();
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber && this.variantChangeUnsubscriber();
      this.removeEventListener('click', this.onClick);
    }

    /** @param {MouseEvent} event */
    onClick(event) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const swatch = event.target instanceof Element
        ? event.target.closest('.color-swatch-img-wrapper')
        : null;

      if (!swatch || !this.contains(swatch)) return;

      event.preventDefault();
      this.changeSiblingProduct(swatch);
    }

    onVariantChange(event) {
      const { target, selectedOptionValues } = event.data;
      if (target?.closest('bundle-product') !== this) return;

      const requestUrl = ProductUtils.buildRequestUrlWithParams(this.dataset.url, null, selectedOptionValues);
      this.renderProductState(requestUrl);
    }

    /**
     * @param {string} productUrl
     * @param {string} view
     * @returns {string}
     */
    buildViewUrl(productUrl, view) {
      const baseUrl = productUrl.startsWith('http')
        ? productUrl
        : `${window.shopUrl || window.location.origin}${productUrl}`;
      const separator = baseUrl.includes('?') ? '&' : '?';

      return `${baseUrl}${separator}view=${view}`;
    }

    /**
     * @param {string} url
     * @returns {Promise<{ element: HTMLElement, doc: Document } | null>}
     */
    async fetchBundleProduct(url) {
      const separator = url.includes('?') ? '&' : '?';
      const response = await fetch(`${url}${separator}view=bundle-product`);

      if (!response.ok) {
        throw new Error(`Bundle product request failed (${response.status})`);
      }

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const element = doc.querySelector('bundle-product');

      return element ? { element, doc } : null;
    }

    /**
     * @param {HTMLElement} swatch
     */
    async changeSiblingProduct(swatch) {
      if (this.isLoading || swatch.classList.contains('tw-color-swatch--active')) return;

      const siblingUrl = swatch.dataset.siblingUrl;
      const siblingProductId = swatch.dataset.siblingProductId;

      if (!siblingUrl || !siblingProductId) return;

      const preservedSize = this.querySelector('[data-type="size"] input:checked')?.value;

      this.isLoading = true;
      this.classList.add('bundle-product--loading');

      try {
        const fetched = await this.fetchBundleProduct(this.buildViewUrl(siblingUrl, 'bundle-product'));

        if (!fetched) return;

        this.applyProductUpdate(fetched.element, { preserveSwatches: true });
        this.updateSizeGuideModal(fetched.element);
        this.setActiveSwatch(siblingProductId);
        this.unavailableIconDisabled();

        if (preservedSize) {
          const sizeInput = this.querySelector(
            `[data-type="size"] input[value="${CSS.escape(preservedSize)}"]:not(.disabled)`
          );

          if (sizeInput) {
            sizeInput.checked = true;

            const selectedOptionValues = Array.from(
              this.querySelectorAll('variant-selects fieldset input:checked:not([data-no-size])')
            ).map(({ dataset }) => dataset.optionValueId).filter(Boolean);
            const requestUrl = ProductUtils.buildRequestUrlWithParams(this.dataset.url, null, selectedOptionValues);
            const sizeFetched = await this.fetchBundleProduct(requestUrl);

            if (sizeFetched) {
              this.applyProductUpdate(sizeFetched.element, { preserveSwatches: true });
            }

            this.setActiveSwatch(siblingProductId);
            this.unavailableIconDisabled();
            publish(PUB_SUB_EVENTS.bundleProductRendered);
            return;
          }
        }

        publish(PUB_SUB_EVENTS.bundleProductRendered);
      } catch (error) {
        console.error('BundleProduct: sibling swap failed', error);
      } finally {
        this.isLoading = false;
        this.classList.remove('bundle-product--loading');
      }
    }

    /**
     * @param {string} url
     * @param {{ preserveSwatches?: boolean }} [options]
     */
    async renderProductState(url, { preserveSwatches = true } = {}) {
      if (this.isLoading) return;

      this.isLoading = true;
      this.classList.add('bundle-product--loading');

      try {
        const fetched = await this.fetchBundleProduct(url);

        if (!fetched) return;

        this.applyProductUpdate(fetched.element, { preserveSwatches: preserveSwatches });

        publish(PUB_SUB_EVENTS.bundleProductRendered);
      } catch (error) {
        console.error('BundleProduct: render failed', error);
      } finally {
        if (this.isConnected) {
          this.isLoading = false;
          this.classList.remove('bundle-product--loading');
        }
      }
    }

    /**
     * @param {HTMLElement} fetchedProduct
     * @param {{ preserveSwatches?: boolean }} [options]
     */
    applyProductUpdate(fetchedProduct, { preserveSwatches = false } = {}) {
      this.dataset.productId = fetchedProduct.dataset.productId;
      this.dataset.url = fetchedProduct.dataset.url;

      const currentSwatches = preserveSwatches
        ? this.querySelector('[data-sibling-color-swatches]')
        : null;

      this.innerHTML = fetchedProduct.innerHTML;

      if (currentSwatches) {
        const fetchedSwatches = this.querySelector('[data-sibling-color-swatches]');

        if (fetchedSwatches) {
          fetchedSwatches.replaceWith(currentSwatches);
        }
      }
    }

    /**
     * Replaces the size guide modal that follows this bundle product after a sibling swap.
     * @param {HTMLElement} fetchedProduct
     */
    updateSizeGuideModal(fetchedProduct) {
      const fetchedModal = fetchedProduct.nextElementSibling;
      const fetchedIsModal = fetchedModal?.matches?.('modal-dialog[id^="PopupModal-size-guide"]');

      const currentModal = this.nextElementSibling;
      const currentIsModal = currentModal?.matches?.('modal-dialog[id^="PopupModal-size-guide"]');

      if (fetchedIsModal && currentIsModal) {
        currentModal.replaceWith(fetchedModal.cloneNode(true));
        return;
      }

      if (fetchedIsModal && !currentIsModal) {
        this.after(fetchedModal.cloneNode(true));
        return;
      }

      if (!fetchedIsModal && currentIsModal) {
        currentModal.remove();
      }
    }

    /**
     * @param {string} productId
     */
    setActiveSwatch(productId) {
      this.querySelectorAll('.color-swatch-img-wrapper').forEach((swatch) => {
        const isActive = swatch.dataset.siblingProductId === productId;
        swatch.classList.toggle('tw-color-swatch--active', isActive);

        if (isActive) {
          swatch.setAttribute('aria-current', 'page');
        } else {
          swatch.removeAttribute('aria-current');
        }
      });
    }

    unavailableIconDisabled() {
      this.querySelectorAll('.color-swatch-img-wrapper').forEach((colorCard) => {
        colorCard.style.opacity = colorCard.getAttribute('data-variant-available') === 'false' ? '0.3' : '1';
      });
    }

  }

  customElements.define('bundle-product', BundleProduct);
}

if(!customElements.get('bundle-product-info')) {

  class BundleProductInfo extends HTMLElement {

    constructor() {
      super();
      this.dialog = null;
      this.content = null;
      this.bundleInfo = null;
      this.activeTrigger = null;
      this.activeBundleProduct = null;
      this.highlightTimeout = null;
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-bundle-product-info-dialog]');
      this.content = this.querySelector('[data-bundle-product-info-content]');
      this.bundleInfo = this.previousElementSibling;

      this.bundleInfo.addEventListener('click', (event) => {
        const trigger = event.target.closest('bundle-details-trigger');
        if (!trigger) return;

        event.preventDefault();
        this.open(trigger);
      });

      this.bundleInfo.addEventListener('keydown', (event) => {
        if (event.code !== 'Enter' && event.code !== 'Space') return;

        const trigger = event.target.closest('bundle-details-trigger');
        if (!trigger) return;

        event.preventDefault();
        this.open(trigger);
      });

      this.dialog.addEventListener('click', (event) => {
        const actionBtn = event.target.closest('[data-bundle-product-info-action]');

        if (actionBtn) {
          actionBtn.dataset.action === 'select-size' ? this.scrollToProduct() : this.close();
          return;
        }

        event.target.closest('[data-bundle-product-info-close]') && this.close();
        event.target === this.dialog && this.close();
      });

      this.dialog.addEventListener('close', () => {
        this.activeTrigger?.focus();
        this.activeTrigger = null;
        this.activeBundleProduct = null;
      });
    }

    async open(trigger) {
      const handle = trigger.dataset.handle;
      const bundleProduct = trigger.closest('bundle-product');

      if (!handle || !bundleProduct) return;

      this.activeTrigger = trigger;
      this.activeBundleProduct = bundleProduct;
      this.toggleAttribute('loading', true);

      const optionValues = Array.from(
        bundleProduct.querySelectorAll('variant-selects fieldset input:checked:not([data-no-size])')
      ).map(({ dataset }) => dataset.optionValueId).filter(Boolean);

      const params = ['view=bundle-product-info'];
      optionValues.length && params.push(`option_values=${optionValues.join(',')}`);

      const response = await fetch(`${window.Shopify.routes.root}products/${handle}?${params.join('&')}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const panel = html.querySelector('[data-bundle-product-info-panel]');

      panel && (this.content.innerHTML = panel.outerHTML);
      this.setFooterAction(bundleProduct);

      this.toggleAttribute('loading', false);
      this.dialog.showModal();
    }

    setFooterAction(bundleProduct) {
      const btn = this.dialog.querySelector('[data-bundle-product-info-action]');
      const hasSize = !!bundleProduct.querySelector('[data-type="size"] input:checked');

      btn.dataset.action = hasSize ? 'continue' : 'select-size';
      btn.textContent = hasSize ? btn.dataset.continueLabel : btn.dataset.selectSizeLabel;
    }

    scrollToProduct() {
      const bundleProduct = this.activeBundleProduct;
      this.close();

      if (!bundleProduct) return;

      bundleProduct.scrollIntoView({ behavior: 'smooth', block: 'center' });

      for (const product of document.querySelectorAll('bundle-product[highlighted]')) {
        product.toggleAttribute('highlighted', false);
      }

      bundleProduct.toggleAttribute('highlighted', true);
      clearTimeout(this.highlightTimeout);
      this.highlightTimeout = setTimeout(() => {
        bundleProduct.toggleAttribute('highlighted', false);
      }, 3000);
    }

    close() {
      this.dialog.close();
    }
  }

  customElements.define('bundle-product-info', BundleProductInfo);
}